import { type RequestServiceOptions, utils } from '@atlaskit/util-service-support';

const DEFAULT_GRAPHQL_ENDPOINT = '/gateway/api/graphql';

/**
 * Copies the gateway tracing headers from the HTTP response (`x-trace-id`, `atl-request-id`)
 * onto the parsed GraphQL result. AGG no longer returns these in the JSON body, so attaching
 * them here lets callers read the trace and request IDs the gateway actually returned.
 */
const attachGatewayTracingHeaders = <Data>(
	response: AggResponse<Data>,
	tracingHeaders: Record<string, string>,
): AggResponse<Data> => {
	if (!response || Object.keys(tracingHeaders).length === 0) {
		return response;
	}
	return {
		...response,
		gatewayTracingHeaders: {
			...response.gatewayTracingHeaders,
			...tracingHeaders,
		},
	};
};

export async function fetchAgg<Variables, Data>({
	graphQuery,
	variables,
	operationName,
	aggAbsoluteUrl: url = DEFAULT_GRAPHQL_ENDPOINT,
	headers,
}: AggRequest<Variables>): Promise<AggResponse<Data>> {
	const capturedTracingHeaders: Record<string, string> = {};
	const options: RequestServiceOptions = {
		requestInit: {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				...headers,
			},
			body: JSON.stringify({
				operationName,
				query: graphQuery,
				variables,
			}),
		},
		queryParams: {
			...(operationName && { q: operationName }),
		},
		// Reported before the response status is inspected, so this also captures the trace
		// for responses that carry GraphQL errors.
		reportTracingHeaders: (tracingHeaders) => {
			Object.assign(capturedTracingHeaders, tracingHeaders);
		},
	};
	const response = await utils.requestService<AggResponse<Data>>({ url }, options);
	return attachGatewayTracingHeaders(response, capturedTracingHeaders);
}

type Maybe<T> = T | null;

export type AggError = {
	message?: Maybe<string>;
	locations?: Maybe<
		Array<{
			line: number;
			column: number;
		}>
	>;
	extensions?: Maybe<{
		errorSource?: Maybe<string>;
		classification?: Maybe<string>;
		statusCode?: Maybe<number>;
		integration?: Maybe<string>;
		product?: Maybe<string>;
	}>;
};

export type AggRequest<Variables> = {
	graphQuery: string;
	variables: Variables;
	operationName?: string;
	aggAbsoluteUrl?: string;
	headers?: Record<string, string>;
};
export interface IncrementalPart<PossibleData> {
	/** Indicates the path in the GraphQL query that the data corresponds to */
	path?: string[];
	data?: Maybe<PossibleData>;
	errors?: Maybe<AggError[]>;
	extensions?: Maybe<AggExtensions>;
}
export interface AggMultipartResponse<FirstPartData, PossibleSubsequentData> {
	/** Present in first part */
	data?: Maybe<FirstPartData>;
	/** Present in first part */
	errors?: Maybe<AggError[]>;
	/** Present in first part */
	extensions?: Maybe<AggExtensions>;

	/** Present after first part */
	incremental?: IncrementalPart<PossibleSubsequentData>[];
	hasNext?: boolean;
}

export interface AggResponse<Data> {
	data?: Maybe<Data>;
	errors?: Maybe<AggError[]>;
	extensions?: Maybe<AggExtensions>;
	/**
	 * Gateway tracing headers (`x-trace-id`, `atl-request-id`) copied off the HTTP response by
	 * `fetchAgg`. AGG no longer returns these in the response body, so this is the only reliable
	 * source of the trace / request ID.
	 */
	gatewayTracingHeaders?: Record<string, string>;
}

export interface AggResults<Edge> {
	edges: Edge[];
	abTest?: Maybe<AggAbTest>;
	totalCount: number;
}

export interface AggEdge<Node> {
	node: Node;
}

export interface AggAbTest {
	abTestId?: Maybe<string>;
	controlId?: Maybe<string>;
	experimentId?: Maybe<string>;
}

export interface Latency {
	started?: Maybe<string>;
	duration?: Maybe<number>;
	service?: Maybe<string>;
	targetRegion?: Maybe<string>;
}

// Marking all optional for safety
export interface AggExtensions {
	gateway?: {
		request_id?: Maybe<string>;
		trace_id?: Maybe<string>;
		crossRegion?: Maybe<boolean>;
		edgeCrossRegion?: Maybe<boolean>;
		duration?: Maybe<number>;
		deprecatedFieldsUsed?: Maybe<any[]>;
		micros_env?: Maybe<string>;
		invokedServices?: Maybe<string[]>;
		schema?: Maybe<{
			schemaName?: Maybe<string>;
			commitHash?: Maybe<string>;
			schemaHash?: Maybe<string>;
			snapshot?: Maybe<string>;
			buildNumber?: Maybe<string>;
			buildTimestamp?: Maybe<string>;
		}>;
		httpCallLatency?: Maybe<Latency[]>;
		httpLatencyPerService?: Maybe<{
			search?: Maybe<number>;
			UppClient?: Maybe<number>;
		}>;
		interestingLatencie?: Maybe<Latency[]>;
	};
}
